import React, {useMemo, useRef, useState} from "react";
import {Row, Select, Switch} from "antd";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import {BBBHitRatiosParams, CalculationType, RatioType} from "../../types/charts";
import {useGetBBBHitRatiosQuery} from "../../redux/api/chartsApiSlice";
import {skipToken} from "@reduxjs/toolkit/query";
import {boundSelectOptions} from "./AnnualHitRatioGraph";
import {roundNumber} from "../../utils/formatters";
import BBBChartCardContainer from "./BBBChartCardContainer";

export default function QuarterHitRatioGraph(params: Pick<BBBHitRatiosParams, 'entity' | 'entity_uuid' | 'uw_year' | 'budget_group' | 'broker_branch'>) {
    const ref = useRef<HighchartsReact.RefObject>(null);
    const [calculationType, setCalculationType] = useState<CalculationType>(CalculationType.NEGOTIATIONS_COUNT)
    const [ratioType, setRatioType] = useState<RatioType>(RatioType.BOUND_OVER_TOTAL)
    const areParamsOk = params.entity && ('entity_uuid' in params ? params.entity_uuid : true)

    const {data, isFetching, isUninitialized} = useGetBBBHitRatiosQuery(areParamsOk ? {
        ...params,
        subdivision: 2,
        calculation_type: calculationType,
        ratio_type: ratioType,
    } : skipToken)

    const cardTitle = useMemo(() => {
        return <Row justify={"space-between"} align={"middle"}>
            <Select
                onSelect={(v: 1 | 2) => setRatioType(v)}
                value={ratioType}
                options={boundSelectOptions}
                style={{width: "200px"}}/>
            <Switch
                checked={calculationType === 1}
                onChange={() => setCalculationType(prevState => prevState === 1 ? 2 : 1)}
                size={"small"}
                checkedChildren={"Trattative"}
                unCheckedChildren={"Premi"}
                style={{width: "77px"}}
            />
        </Row>
    }, [calculationType, ratioType])


    const chartOptions = useMemo(() => {
        const series = []
        let categories
        if (data) {
            for (const [key, options] of Object.entries(data)) {
                series.push({name: key, data: options.map(el => el.amount * 100), yAxis: 0})
                categories = options.map(el => el.label)
            }
        }
        return {
            chart: {
                type: 'column',
                height: "40%"
            },
            title: {
                text: '',
                align: 'left'
            },
            subtitle: {
                text: ""
            },
            xAxis: {
                categories,
                accessibility: {
                    description: 'Quarter'
                }
            },
            yAxis: [{
                min: 0,
                max: 100,
                title: {
                    text: 'Percentuale (%)',
                }
            }],
            plotOptions: {
                column: {
                    pointPadding: 0.2,
                    borderWidth: 0,
                    borderRadius: 5,
                    dataLabels: {
                        enabled: true,
                        format: '{point.y:.0f}%',
                    }
                }
            },
            series,
            tooltip: {
                // @ts-ignore
                formatter: function () {
                    // @ts-ignore
                    const value = roundNumber(this.y || 0)
                    // @ts-ignore
                    const symbol = this.color ? `<span style='color: ${this.color}'>●</span>` : ''
                    // @ts-ignore
                    return `${symbol} <span>${this.series.name}</span>: <span style='font-weight: bolder'>${(value)}%</span>`
                }
            },
            exporting: {
                enabled: false
            },
            credits: undefined,
        }
    }, [data])

    return <BBBChartCardContainer
        loading={isFetching || isUninitialized}
        title={cardTitle}>
        <Row justify={"center"}>
            <h2>Hit Ratio per Quarter</h2>
        </Row>
        <HighchartsReact
            highcharts={Highcharts}
            ref={ref}
            options={chartOptions}
        />
    </BBBChartCardContainer>
}